<template>
  <div class="p-6 max-w-full">
    <!-- Título -->
    <div class="mb-6 flex items-center justify-between">
      <div>
        <h1 class="text-2xl font-bold text-gray-900 dark:text-white mb-1">{{ modoEdicion ? 'Editar Pedido' : 'Nuevo Pedido' }}</h1>
        <p class="text-gray-500 dark:text-gray-400 text-sm">POS2 — {{ modoEdicion ? 'Actualización de pedido' : 'Alta de pedido' }}</p>
      </div>
      <div class="flex gap-2">
        <button @click="router.back()" class="px-4 py-2 rounded bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 text-sm font-semibold hover:bg-gray-300 dark:hover:bg-gray-600 transition">
          Cancelar
        </button>
        <template v-if="!bloqueado">
          <button @click="guardar('guardado')" :disabled="guardando || cargandoPedido" class="px-4 py-2 rounded bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 disabled:opacity-50 transition">
            {{ guardando ? 'Guardando...' : 'Guardar Pedido' }}
          </button>
          <button v-if="false && !modoEdicion" @click="guardar('cotizacion')" :disabled="guardando" class="px-4 py-2 rounded bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50 transition">
            Guardar y Generar Cotización
          </button>
          <button v-if="!modoEdicion" @click="guardarYGenerarOrden" :disabled="guardando || generandoOrden" class="px-4 py-2 rounded bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50 transition">
            {{ guardando || generandoOrden ? 'Procesando...' : 'Guardar y Generar Orden de Compra' }}
          </button>
        </template>
      </div>
    </div>

    <!-- Cargando -->
    <div v-if="cargandoPedido" class="mb-4 px-4 py-3 bg-gray-50 dark:bg-gray-700 rounded text-sm text-gray-500 dark:text-gray-400">
      Cargando pedido...
    </div>

    <!-- Aviso bloqueado -->
    <div v-if="bloqueado" class="mb-4 px-4 py-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded text-sm text-amber-700 dark:text-amber-400 flex items-center gap-2">
      <svg class="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/></svg>
      Este pedido tiene una orden de producción activa o en proceso. No se puede editar hasta que la orden sea completada o cancelada.
    </div>

    <!-- Error global -->
    <div v-if="errorGlobal" class="mb-4 px-4 py-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded text-sm text-red-700 dark:text-red-400">
      {{ errorGlobal }}
    </div>

    <!-- Header / Datos generales -->
    <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-5 mb-4">
      <h2 class="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">Datos Generales</h2>
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">

        <!-- Cliente -->
        <div class="sm:col-span-2">
          <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Cliente <span class="text-red-500">*</span></label>
          <div class="flex gap-2">
            <input
              v-model="header.clienteNombre"
              type="text"
              readonly
              placeholder="Seleccionar cliente..."
              class="flex-1 px-3 py-2 border rounded text-sm bg-gray-50 dark:bg-gray-900 dark:border-gray-700 focus:outline-none"
              :class="bloqueado ? 'cursor-default opacity-60' : 'cursor-pointer'"
              @click="!bloqueado && abrirPopupCliente()"
            />
            <button :disabled="bloqueado" @click="abrirPopupCliente" class="px-3 py-2 rounded bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-400 text-sm hover:bg-violet-200 transition disabled:opacity-40 disabled:cursor-not-allowed">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg>
            </button>
          </div>
        </div>

        <!-- Fecha Alta -->
        <div>
          <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Fecha Alta <span class="text-red-500">*</span></label>
          <input v-model="header.fecha_alta" :disabled="bloqueado" type="datetime-local" class="w-full px-3 py-2 border rounded text-sm bg-gray-50 dark:bg-gray-900 dark:border-gray-700 focus:outline-none focus:ring-1 focus:ring-violet-400 disabled:opacity-60" />
        </div>

        <!-- Fecha Entrega -->
        <div>
          <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Fecha Entrega</label>
          <input v-model="header.fecha_entrega" :disabled="bloqueado" type="datetime-local" class="w-full px-3 py-2 border rounded text-sm bg-gray-50 dark:bg-gray-900 dark:border-gray-700 focus:outline-none focus:ring-1 focus:ring-violet-400 disabled:opacity-60" />
        </div>

        <!-- Tipo Entrega -->
        <div>
          <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Tipo de Entrega</label>
          <select v-model="header.tipo_entrega" :disabled="bloqueado" class="w-full px-3 py-2 border rounded text-sm bg-gray-50 dark:bg-gray-900 dark:border-gray-700 focus:outline-none focus:ring-1 focus:ring-violet-400 disabled:opacity-60">
            <option value="recepcion">Recepción en tienda</option>
            <option value="domicilio">A domicilio</option>
          </select>
        </div>

        <!-- Total Venta -->
        <div>
          <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Total Venta</label>
          <input :value="formatMoney(totalVenta)" type="text" readonly class="w-full px-3 py-2 border rounded text-sm bg-gray-100 dark:bg-gray-700 dark:border-gray-600 text-gray-700 dark:text-gray-300 cursor-default font-semibold" />
        </div>

        <!-- Esquema Facturación -->
        <div>
          <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Esquema Facturación</label>
          <select v-model="header.esquema_facturacion_id" class="w-full px-3 py-2 border rounded text-sm bg-gray-50 dark:bg-gray-900 dark:border-gray-700 focus:outline-none focus:ring-1 focus:ring-violet-400">
            <option :value="null">— Sin esquema —</option>
            <option v-for="e in catalogos.esquemas" :key="e.id" :value="e.id">{{ e.nombre }}</option>
          </select>
        </div>

        <!-- % Descuento Global -->
        <div>
          <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">% Descuento Global</label>
          <input v-model.number="header.descuento_global" :disabled="bloqueado" type="number" min="0" max="100" step="0.01" class="w-full px-3 py-2 border rounded text-sm bg-gray-50 dark:bg-gray-900 dark:border-gray-700 focus:outline-none focus:ring-1 focus:ring-violet-400 disabled:opacity-60" />
        </div>

        <!-- Vendedor -->
        <div>
          <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Vendedor</label>
          <select v-model="header.vendedor_id" :disabled="bloqueado || !header.cliente_id" class="w-full px-3 py-2 border rounded text-sm bg-gray-50 dark:bg-gray-900 dark:border-gray-700 focus:outline-none focus:ring-1 focus:ring-violet-400 disabled:opacity-50">
            <option :value="null">— Sin vendedor —</option>
            <option v-for="v in vendedores" :key="v.id" :value="v.id">{{ v.nombre }} {{ v.apellido_paterno }}</option>
          </select>
        </div>

        <!-- Diseñador -->
        <div>
          <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Diseñador</label>
          <select v-model="header.disenador_id" :disabled="bloqueado" class="w-full px-3 py-2 border rounded text-sm bg-gray-50 dark:bg-gray-900 dark:border-gray-700 focus:outline-none focus:ring-1 focus:ring-violet-400 disabled:opacity-60">
            <option :value="null">— Sin diseñador —</option>
            <option v-for="d in catalogos.disenadores" :key="d.id" :value="d.id">{{ d.nombre }}</option>
          </select>
        </div>
      </div>
    </div>

    <!-- Sección Detalle -->
    <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 mb-4">
      <div class="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
        <h2 class="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Detalle del Pedido</h2>
        <button v-if="!bloqueado" @click="abrirPopupProductos" class="px-3 py-1.5 rounded bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 transition flex items-center gap-1">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/></svg>
          Agregar Producto
        </button>
      </div>

      <div class="overflow-x-auto">
        <table class="w-full text-xs text-left min-w-[1400px]">
          <thead class="bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 uppercase tracking-wider">
            <tr>
              <th class="px-3 py-2 w-40">Producto</th>
              <th class="px-3 py-2 w-28">Precio UM</th>
              <th class="px-3 py-2 w-24">Desc. %</th>
              <th class="px-3 py-2 w-24">Cant. Pzas</th>
              <th class="px-3 py-2 w-24">Ancho m</th>
              <th class="px-3 py-2 w-24">Alto m</th>
              <th class="px-3 py-2 w-20 text-center">Req. Conv.</th>
              <th class="px-3 py-2 w-40">Comentarios</th>
              <th class="px-3 py-2 w-40">Nombre Archivo</th>
              <th class="px-3 py-2 w-40">Acabados</th>
              <th class="px-3 py-2 w-32">Frente/Vuelta</th>
              <th class="px-3 py-2 w-28 text-right">Precio Pieza</th>
              <th class="px-3 py-2 w-10"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="detalles.length === 0">
              <td colspan="13" class="px-4 py-8 text-center text-gray-400">Sin productos. Haz clic en "Agregar Producto" para comenzar.</td>
            </tr>
            <tr
              v-for="(det, idx) in detalles"
              :key="idx"
              class="border-b dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-violet-50/30 dark:hover:bg-violet-900/10"
            >
              <!-- Producto nombre (readonly) -->
              <td class="px-3 py-1.5 font-medium text-gray-800 dark:text-gray-200">{{ det.producto_nombre_orig }}</td>

              <!-- Precio UM -->
              <td class="px-1 py-1">
                <input v-model.number="det.precio_um" @input="calcularPrecioPieza(det)" type="number" min="0" step="0.0001"
                  class="w-full px-2 py-1 border border-gray-200 dark:border-gray-600 rounded text-xs dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-1 focus:ring-violet-400" />
              </td>

              <!-- Descuento -->
              <td class="px-1 py-1">
                <input v-model.number="det.descuento" @input="calcularPrecioPieza(det)" type="number" min="0" max="100" step="0.01"
                  class="w-full px-2 py-1 border border-gray-200 dark:border-gray-600 rounded text-xs dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-1 focus:ring-violet-400" />
              </td>

              <!-- Cantidad Piezas -->
              <td class="px-1 py-1">
                <input v-model.number="det.cantidad_piezas" @input="calcularPrecioPieza(det)" type="number" min="0" step="0.01"
                  class="w-full px-2 py-1 border border-gray-200 dark:border-gray-600 rounded text-xs dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-1 focus:ring-violet-400" />
              </td>

              <!-- Ancho -->
              <td class="px-1 py-1">
                <input v-model.number="det.ancho_metros" type="number" min="0" step="0.0001"
                  class="w-full px-2 py-1 border border-gray-200 dark:border-gray-600 rounded text-xs dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-1 focus:ring-violet-400" />
              </td>

              <!-- Alto -->
              <td class="px-1 py-1">
                <input v-model.number="det.alto_metros" type="number" min="0" step="0.0001"
                  class="w-full px-2 py-1 border border-gray-200 dark:border-gray-600 rounded text-xs dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-1 focus:ring-violet-400" />
              </td>

              <!-- Requiere Conversión -->
              <td class="px-1 py-1 text-center">
                <input v-model="det.requiere_conversion" type="checkbox"
                  class="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-violet-600 focus:ring-violet-400" />
              </td>

              <!-- Comentarios -->
              <td class="px-1 py-1">
                <input v-model="det.comentarios" type="text" maxlength="255" placeholder="Comentarios..."
                  class="w-full px-2 py-1 border border-gray-200 dark:border-gray-600 rounded text-xs dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-1 focus:ring-violet-400" />
              </td>

              <!-- Nombre Archivo -->
              <td class="px-1 py-1">
                <input v-model="det.nombre_archivo" type="text" maxlength="255" placeholder="Nombre archivo..."
                  class="w-full px-2 py-1 border border-gray-200 dark:border-gray-600 rounded text-xs dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-1 focus:ring-violet-400" />
              </td>

              <!-- Acabados -->
              <td class="px-1 py-1">
                <input v-model="det.acabados" type="text" maxlength="255" placeholder="Acabados..."
                  class="w-full px-2 py-1 border border-gray-200 dark:border-gray-600 rounded text-xs dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-1 focus:ring-violet-400" />
              </td>

              <!-- Frente/Vuelta -->
              <td class="px-1 py-1">
                <select v-model="det.frente_vuelta"
                  class="w-full px-2 py-1 border border-gray-200 dark:border-gray-600 rounded text-xs dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-1 focus:ring-violet-400">
                  <option>Frente</option>
                  <option>Vuelta</option>
                  <option>Frente y Vuelta</option>
                </select>
              </td>

              <!-- Precio Pieza (readonly) -->
              <td class="px-3 py-1.5 text-right font-semibold text-gray-800 dark:text-gray-200">
                {{ formatMoney(det.precio_pieza) }}
              </td>

              <!-- Eliminar -->
              <td class="px-1 py-1">
                <button v-if="!bloqueado" @click="eliminarDetalle(idx)" class="text-red-400 hover:text-red-600 p-1">
                  <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
                </button>
              </td>
            </tr>
          </tbody>
          <tfoot v-if="detalles.length > 0">
            <tr class="bg-gray-50 dark:bg-gray-700 font-semibold">
              <td colspan="11" class="px-3 py-2 text-right text-sm text-gray-600 dark:text-gray-300">Total:</td>
              <td class="px-3 py-2 text-right text-sm text-violet-700 dark:text-violet-400">{{ formatMoney(totalVenta) }}</td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>

    <!-- ==================== POPUP CLIENTE ==================== -->
    <div v-if="popupCliente.visible" class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div class="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-4xl flex flex-col" style="max-height: 85vh;">

        <!-- Header -->
        <div class="flex items-center justify-between px-5 py-4 border-b dark:border-gray-700 shrink-0">
          <h3 class="font-semibold text-gray-900 dark:text-white text-base">Seleccionar Cliente</h3>
          <button @click="popupCliente.visible = false" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
          </button>
        </div>

        <!-- Buscador -->
        <div class="px-5 py-3 border-b dark:border-gray-700 shrink-0">
          <div class="flex gap-2">
            <input
              v-model="popupCliente.buscar"
              @keyup.enter="buscarClientes"
              type="text"
              placeholder="Buscar por nombre, RFC o correo..."
              autofocus
              class="flex-1 px-3 py-2 border rounded text-sm dark:bg-gray-900 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-violet-300"
            />
            <button @click="buscarClientes" class="px-4 py-2 rounded bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 transition">Buscar</button>
          </div>
          <p v-if="popupCliente.resultados.length > 0" class="text-xs text-gray-400 mt-1.5">
            {{ popupCliente.resultados.length }} resultado(s) — haz clic en una card para seleccionar
          </p>
        </div>

        <!-- Grid de cards -->
        <div class="flex-1 overflow-y-auto p-4">
          <div v-if="popupCliente.cargando" class="py-10 text-center text-gray-400 text-sm">Buscando clientes...</div>

          <div v-else-if="popupCliente.resultados.length === 0 && popupCliente.buscado" class="py-10 text-center text-gray-400 text-sm">
            No se encontraron clientes con ese criterio
          </div>

          <div v-else-if="popupCliente.resultados.length === 0" class="py-10 text-center text-gray-400 text-sm">
            Escribe un nombre y presiona Buscar
          </div>

          <div v-else class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <div
              v-for="c in popupCliente.resultados"
              :key="c.id"
              @click="seleccionarCliente(c)"
              class="cursor-pointer rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 p-4 hover:border-violet-400 hover:shadow-md transition group"
            >
              <!-- Nombre + abreviatura -->
              <div class="flex items-start justify-between gap-2 mb-2">
                <span class="font-semibold text-sm text-gray-900 dark:text-white group-hover:text-violet-700 dark:group-hover:text-violet-400 leading-tight">
                  {{ c.nombre }}
                </span>
                <span v-if="c.abreviatura" class="shrink-0 text-xs bg-gray-100 dark:bg-gray-600 text-gray-500 dark:text-gray-300 px-1.5 py-0.5 rounded font-mono">
                  {{ c.abreviatura }}
                </span>
              </div>

              <!-- RFC + correo -->
              <div class="space-y-0.5 text-xs text-gray-500 dark:text-gray-400 mb-2">
                <div v-if="c.rfc" class="flex items-center gap-1">
                  <svg class="w-3 h-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
                  <span class="font-mono">{{ c.rfc }}</span>
                </div>
                <div v-if="c.correo" class="flex items-center gap-1 truncate">
                  <svg class="w-3 h-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>
                  <span class="truncate">{{ c.correo }}</span>
                </div>
                <div v-if="c.telefono" class="flex items-center gap-1">
                  <svg class="w-3 h-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.948V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/></svg>
                  <span>{{ c.telefono }}</span>
                </div>
              </div>

              <!-- Ubicación -->
              <div v-if="c.municipio || c.estado" class="text-xs text-gray-400 dark:text-gray-500 flex items-center gap-1 mb-2">
                <svg class="w-3 h-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
                <span>{{ [c.municipio, c.estado].filter(Boolean).join(', ') }}</span>
              </div>

              <!-- Badges: descuento + días crédito -->
              <div class="flex items-center gap-2 flex-wrap">
                <span v-if="c.descuento > 0" class="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-2 py-0.5 rounded-full font-medium">
                  {{ c.descuento }}% desc.
                </span>
                <span v-if="c.dias_credito > 0" class="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 px-2 py-0.5 rounded-full font-medium">
                  {{ c.dias_credito }}d crédito
                </span>
                <span v-if="c.tipo_cliente" class="text-xs bg-gray-100 dark:bg-gray-600 text-gray-500 dark:text-gray-300 px-2 py-0.5 rounded-full">
                  {{ c.tipo_cliente.nombre }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ==================== CONFIRM ORDEN PRODUCCIÓN ==================== -->
    <div v-if="confirmOrden.visible" class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div class="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-sm p-6 text-center">
        <div class="w-12 h-12 rounded-full bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center mx-auto mb-4">
          <svg class="w-6 h-6 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
        </div>
        <h3 class="text-lg font-bold text-gray-900 dark:text-white mb-2">Pedido guardado</h3>
        <p class="text-sm text-gray-500 dark:text-gray-400 mb-6">¿Deseas generar la orden de producción ahora?</p>
        <div class="flex gap-3">
          <button @click="rechazarOrden" class="flex-1 px-4 py-2 rounded border border-gray-300 dark:border-gray-600 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition">
            No, ahora no
          </button>
          <button @click="generarOrden" :disabled="generandoOrden" class="flex-1 px-4 py-2 rounded bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 disabled:opacity-50 transition">
            {{ generandoOrden ? 'Generando...' : 'Sí, generar' }}
          </button>
        </div>
      </div>
    </div>

    <!-- ==================== MODAL HOJA PRODUCCIÓN ==================== -->
    <ModalHojaProduccion
      :visible="modalHoja.visible"
      :orden="modalHoja.orden"
      @cerrar="cerrarModalYRedirigir"
    />

    <!-- ==================== POPUP PRODUCTOS ==================== -->
    <div v-if="popupProductos.visible" class="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div class="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-3xl mx-4 flex flex-col" style="max-height: 85vh;">
        <!-- Header popup -->
        <div class="flex items-center justify-between px-5 py-4 border-b dark:border-gray-700 shrink-0">
          <div>
            <h3 class="font-semibold text-gray-900 dark:text-white">Seleccionar Producto</h3>
            <!-- Breadcrumb -->
            <div class="flex items-center gap-1 mt-1 text-xs text-gray-500 dark:text-gray-400">
              <button @click="navegarInicio" class="hover:text-violet-600">Inicio</button>
              <template v-for="(crumb, ci) in popupProductos.breadcrumb" :key="ci">
                <span>›</span>
                <button @click="navegarBreadcrumb(ci)" class="hover:text-violet-600 max-w-[120px] truncate">{{ crumb.nombre }}</button>
              </template>
            </div>
          </div>
          <button @click="popupProductos.visible = false" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
          </button>
        </div>

        <!-- Buscador en nivel actual -->
        <div class="px-5 py-3 border-b dark:border-gray-700 shrink-0">
          <input
            v-model="popupProductos.filtro"
            type="text"
            placeholder="Filtrar en este nivel..."
            class="w-full px-3 py-2 border rounded text-sm dark:bg-gray-900 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-violet-300"
          />
        </div>

        <!-- Grid de cards -->
        <div class="flex-1 overflow-y-auto p-4">
          <div v-if="popupProductos.cargando" class="py-10 text-center text-gray-400">Cargando catálogo...</div>
          <div v-else-if="itemsNivelFiltrado.length === 0" class="py-10 text-center text-gray-400">Sin elementos</div>
          <div v-else class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            <div
              v-for="item in itemsNivelFiltrado"
              :key="item._key"
              @click="clickItem(item)"
              class="cursor-pointer rounded-lg border p-3 flex flex-col items-center gap-2 transition hover:shadow-md"
              :class="item._tipo === 'categoria'
                ? 'bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 hover:border-violet-400'
                : 'bg-violet-50 dark:bg-violet-900/20 border-violet-200 dark:border-violet-700 hover:border-violet-500'"
            >
              <span class="text-3xl">{{ item._tipo === 'categoria' ? '📁' : '📦' }}</span>
              <span class="text-xs font-medium text-gray-800 dark:text-gray-200 text-center leading-tight">{{ item.nombre }}</span>
              <span v-if="item._tipo === 'categoria'" class="text-xs text-gray-400">
                {{ (item.hijos_recursivos?.length ?? 0) + (item.productos?.length ?? 0) }} elementos
              </span>
              <span v-else class="text-xs font-semibold text-violet-700 dark:text-violet-400">
                {{ formatMoney(item.precio ?? 0) }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useHttp } from '@/composables/useHttp.js'
import ModalHojaProduccion from './ModalHojaProduccion.vue'

const router = useRouter()
const route  = useRoute()

const pedidoId  = computed(() => route.params.id ?? null)
const modoEdicion = computed(() => !!pedidoId.value)

// Instancias separadas de useHttp para evitar colisión
const httpPedido    = useHttp()
const httpCats      = useHttp()
const httpVendedores = useHttp()
const httpCatalogos = useHttp()
const httpOrden     = useHttp()

// ─── Estado header ───────────────────────────────────────────────
const header = ref({
  cliente_id:             null,
  clienteNombre:          '',
  fecha_alta:             new Date().toISOString().slice(0, 16),
  fecha_entrega:          '',
  tipo_entrega:           'recepcion',
  esquema_facturacion_id: null,
  descuento_global:       0,
  vendedor_id:            null,
  disenador_id:           null,
})

const vendedores     = ref([])
const catalogos      = ref({ esquemas: [], disenadores: [] })
const detalles       = ref([])
const guardando      = ref(false)
const cargandoPedido = ref(false)
const errorGlobal    = ref('')
const pedidoGuardado = ref(null)
const confirmOrden   = ref({ visible: false })
const generandoOrden = ref(false)
const modalHoja      = ref({ visible: false, orden: {} })
const bloqueado      = ref(false)   // true si tiene orden activa/en_proceso

// ─── Total venta ─────────────────────────────────────────────────
const totalVenta = computed(() =>
  detalles.value.reduce((sum, d) => sum + (d.precio_pieza ?? 0), 0)
)

// ─── Helpers ─────────────────────────────────────────────────────
const formatMoney = (val) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(val ?? 0)

const calcularPrecioPieza = (det) => {
  det.precio_pieza = (det.precio_um ?? 0) * (det.cantidad_piezas ?? 1) * (1 - ((det.descuento ?? 0) / 100))
}

const eliminarDetalle = (idx) => detalles.value.splice(idx, 1)

// ─── Cargar catálogos auxiliares ─────────────────────────────────
const cargarCatalogos = async () => {
  await httpCatalogos.get('/pos2/esquemas-facturacion?per_page=200')
  catalogos.value.esquemas = httpCatalogos.data.value?.data ?? []

  await httpCatalogos.get('/pos2/disenadores?per_page=200')
  catalogos.value.disenadores = httpCatalogos.data.value?.data ?? []
}

// ─── Popup Cliente ───────────────────────────────────────────────
const popupCliente = ref({ visible: false, buscar: '', resultados: [], cargando: false, buscado: false })

const abrirPopupCliente = () => {
  popupCliente.value = { visible: true, buscar: '', resultados: [], cargando: false, buscado: false }
}

const buscarClientes = async () => {
  if (!popupCliente.value.buscar.trim()) return
  popupCliente.value.cargando = true
  try {
    await httpCatalogos.get(`/pos2/clientes?buscar=${encodeURIComponent(popupCliente.value.buscar)}&per_page=30&with_tipo=1`)
    popupCliente.value.resultados = httpCatalogos.data.value?.data ?? []
    popupCliente.value.buscado = true
  } finally {
    popupCliente.value.cargando = false
  }
}

const seleccionarCliente = async (cliente) => {
  header.value.cliente_id    = cliente.id
  header.value.clienteNombre = cliente.nombre
  header.value.vendedor_id   = null
  popupCliente.value.visible = false

  // Cargar vendedores del cliente
  try {
    await httpVendedores.get(`/pos2/clientes/${cliente.id}/vendedores`)
    vendedores.value = httpVendedores.data.value ?? []
  } catch {
    vendedores.value = []
  }
}

// ─── Popup Productos ─────────────────────────────────────────────
const popupProductos = ref({
  visible:    false,
  cargando:   false,
  filtro:     '',
  arbol:      [],       // raíces completas (cargado una vez)
  nivelActual: [],      // nodos del nivel en vista
  breadcrumb: [],       // [{ id, nombre, nodo }]
})

const itemsNivelFiltrado = computed(() => {
  const filtro = popupProductos.value.filtro.toLowerCase()
  return popupProductos.value.nivelActual.filter(item =>
    !filtro || item.nombre.toLowerCase().includes(filtro)
  )
})

const abrirPopupProductos = async () => {
  popupProductos.value.visible   = true
  popupProductos.value.filtro    = ''
  popupProductos.value.breadcrumb = []

  if (popupProductos.value.arbol.length === 0) {
    popupProductos.value.cargando = true
    try {
      await httpCats.get('/pos2/categorias/arbol-completo')
      popupProductos.value.arbol = httpCats.data.value ?? []
    } finally {
      popupProductos.value.cargando = false
    }
  }

  // Nivel raíz: todas las categorías raíz
  popupProductos.value.nivelActual = buildNivel(popupProductos.value.arbol)
}

// Construye el array de items para un nivel dado (categorías + productos)
const buildNivel = (categorias, productosDirectos = []) => {
  const cats = categorias.map(c => ({ ...c, _tipo: 'categoria', _key: `cat-${c.id}` }))
  const prods = productosDirectos.map(p => ({ ...p, _tipo: 'producto', _key: `prod-${p.id}` }))
  return [...cats, ...prods]
}

const navegarInicio = () => {
  popupProductos.value.breadcrumb  = []
  popupProductos.value.filtro      = ''
  popupProductos.value.nivelActual = buildNivel(popupProductos.value.arbol)
}

const navegarBreadcrumb = (idx) => {
  const crumb = popupProductos.value.breadcrumb[idx]
  popupProductos.value.breadcrumb  = popupProductos.value.breadcrumb.slice(0, idx + 1)
  popupProductos.value.filtro      = ''
  popupProductos.value.nivelActual = buildNivel(crumb.nodo.hijos_recursivos ?? [], crumb.nodo.productos ?? [])
}

const clickItem = (item) => {
  if (item._tipo === 'categoria') {
    popupProductos.value.breadcrumb.push({ id: item.id, nombre: item.nombre, nodo: item })
    popupProductos.value.filtro      = ''
    popupProductos.value.nivelActual = buildNivel(item.hijos_recursivos ?? [], item.productos ?? [])
  } else {
    agregarProducto(item)
    popupProductos.value.visible = false
  }
}

const agregarProducto = (producto) => {
  detalles.value.push({
    producto_id:            producto.id,
    producto_nombre_orig:   producto.nombre,
    precio_um:              Number(producto.precio ?? 0),
    precio_um_orig:         Number(producto.precio ?? 0),
    descuento:              0,
    cantidad_piezas:        1,
    ancho_metros:           null,
    alto_metros:            null,
    requiere_conversion:    producto.requiere_conversion ?? false,
    requiere_conversion_orig: producto.requiere_conversion ?? false,
    comentarios:            '',
    nombre_archivo:         '',
    acabados:               '',
    frente_vuelta:          'Frente',
    precio_pieza:           Number(producto.precio ?? 0),
  })
}

// ─── Guardar pedido ───────────────────────────────────────────────
const guardar = async (estado) => {
  errorGlobal.value = ''

  if (!header.value.cliente_id) {
    errorGlobal.value = 'Debes seleccionar un cliente'
    return
  }
  if (!header.value.fecha_alta) {
    errorGlobal.value = 'La fecha de alta es obligatoria'
    return
  }

  guardando.value = true
  try {
    const payload = {
      cliente_id:             header.value.cliente_id,
      fecha_alta:             header.value.fecha_alta,
      fecha_entrega:          header.value.fecha_entrega || null,
      tipo_entrega:           header.value.tipo_entrega,
      esquema_facturacion_id: header.value.esquema_facturacion_id,
      descuento_global:       header.value.descuento_global,
      vendedor_id:            header.value.vendedor_id,
      disenador_id:           header.value.disenador_id,
      estado,
      detalles:               detalles.value,
    }

    if (modoEdicion.value) {
      await httpPedido.put(`/pos2/pedidos/${pedidoId.value}`, payload)
      router.push('/pos2/pedidos')
    } else {
      await httpPedido.post('/pos2/pedidos', payload)
      pedidoGuardado.value = httpPedido.data.value
      confirmOrden.value = { visible: true }
    }
  } catch (err) {
    const errors = err.response?.data?.errors
    errorGlobal.value = errors
      ? Object.values(errors).flat().join(' | ')
      : (err.response?.data?.message ?? 'Error al guardar el pedido')
  } finally {
    guardando.value = false
  }
}

const rechazarOrden = () => {
  confirmOrden.value.visible = false
  router.push('/pos2/pedidos')
}

// Flujo combinado: guarda el pedido y, si todo sale bien, genera la orden
// de producción inmediatamente sin pasar por el modal de confirmación.
const guardarYGenerarOrden = async () => {
  errorGlobal.value = ''

  if (!header.value.cliente_id) {
    errorGlobal.value = 'Debes seleccionar un cliente'
    return
  }
  if (!header.value.fecha_alta) {
    errorGlobal.value = 'La fecha de alta es obligatoria'
    return
  }

  guardando.value = true
  try {
    const payload = {
      cliente_id:             header.value.cliente_id,
      fecha_alta:             header.value.fecha_alta,
      fecha_entrega:          header.value.fecha_entrega || null,
      tipo_entrega:           header.value.tipo_entrega,
      esquema_facturacion_id: header.value.esquema_facturacion_id,
      descuento_global:       header.value.descuento_global,
      vendedor_id:            header.value.vendedor_id,
      disenador_id:           header.value.disenador_id,
      estado:                 'guardado',
      detalles:               detalles.value,
    }

    await httpPedido.post('/pos2/pedidos', payload)
    pedidoGuardado.value = httpPedido.data.value
  } catch (err) {
    const errors = err.response?.data?.errors
    errorGlobal.value = errors
      ? Object.values(errors).flat().join(' | ')
      : (err.response?.data?.message ?? 'Error al guardar el pedido')
    return
  } finally {
    guardando.value = false
  }

  // Si el pedido se guardó, encadenamos la generación de la orden.
  if (pedidoGuardado.value?.id) {
    await generarOrden()
  }
}

const generarOrden = async () => {
  generandoOrden.value = true
  try {
    await httpOrden.post(`/pos2/pedidos/${pedidoGuardado.value.id}/orden-produccion`, {})
    const orden = httpOrden.data.value
    confirmOrden.value.visible = false
    modalHoja.value = { visible: true, orden }
  } catch (err) {
    alert(err.response?.data?.message ?? 'Error al generar la orden')
  } finally {
    generandoOrden.value = false
  }
}

const cerrarModalYRedirigir = () => {
  modalHoja.value.visible = false
  router.push('/pos2/pedidos')
}

// ─── Cargar pedido existente (modo edición) ───────────────────────
const cargarPedido = async () => {
  cargandoPedido.value = true
  try {
    await httpPedido.get(`/pos2/pedidos/${pedidoId.value}`)
    const p = httpPedido.data.value

    // Detectar si tiene orden activa o en proceso
    const orden = p.orden_produccion
    bloqueado.value = orden && ['activa', 'en_proceso'].includes(orden.estado)

    header.value = {
      cliente_id:             p.cliente_id,
      clienteNombre:          p.cliente?.nombre ?? '',
      fecha_alta:             p.fecha_alta ? p.fecha_alta.slice(0, 16) : '',
      fecha_entrega:          p.fecha_entrega ? p.fecha_entrega.slice(0, 16) : '',
      tipo_entrega:           p.tipo_entrega ?? 'recepcion',
      esquema_facturacion_id: p.esquema_facturacion_id ?? null,
      descuento_global:       p.descuento_global ?? 0,
      vendedor_id:            p.vendedor_id ?? null,
      disenador_id:           p.disenador_id ?? null,
    }

    // Cargar vendedores del cliente
    try {
      await httpVendedores.get(`/pos2/clientes/${p.cliente_id}/vendedores`)
      vendedores.value = httpVendedores.data.value ?? []
    } catch {
      vendedores.value = []
    }

    detalles.value = (p.detalles ?? []).map(d => ({
      producto_id:              d.producto_id,
      producto_nombre_orig:     d.producto?.nombre ?? d.producto_nombre_orig ?? '',
      precio_um:                Number(d.precio_um ?? 0),
      precio_um_orig:           Number(d.precio_um ?? 0),
      descuento:                Number(d.descuento ?? 0),
      cantidad_piezas:          Number(d.cantidad_piezas ?? 1),
      ancho_metros:             d.ancho_metros ?? null,
      alto_metros:              d.alto_metros ?? null,
      requiere_conversion:      d.requiere_conversion ?? false,
      requiere_conversion_orig: d.requiere_conversion ?? false,
      comentarios:              d.comentarios ?? '',
      nombre_archivo:           d.nombre_archivo ?? '',
      acabados:                 d.acabados ?? '',
      frente_vuelta:            d.frente_vuelta ?? 'Frente',
      precio_pieza:             Number(d.precio_pieza ?? 0),
    }))
  } catch {
    errorGlobal.value = 'Error al cargar el pedido'
  } finally {
    cargandoPedido.value = false
  }
}

onMounted(async () => {
  await cargarCatalogos()
  if (modoEdicion.value) cargarPedido()
})
</script>
